"use client";

import * as React from "react";
import Link from "next/link";
import {
  Bookmark,
  BookmarkCheck,
  Copy,
  Disc3,
  Download,
  MonitorSmartphone,
  Link2,
  ListEnd,
  ListPlus,
  MoreHorizontal,
  Mic2,
  Play,
  Share2,
  Users,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@gerama/ui";
import type { Song } from "@gerama/types";
import { cn } from "@gerama/utils";
import { ROLE_LABEL, useSongActions } from "./use-song-actions";
import { BRAND } from "@/lib/brand";
import { artistPath } from "@/features/artists/artist-path";
import { publicPath } from "@/lib/public-path";

/**
 * The "…" menu on a song — rows, the song page and the player share it.
 * Play / add to queue, playlists and My Music, then navigation (artists, album,
 * credits) and the desktop app / share group. Album and full credits come from
 * the detail request, made only once the menu opens.
 */
export function SongContextMenu({
  song,
  className,
  triggerClassName,
  children,
}: {
  song: Song;
  className?: string;
  triggerClassName?: string;
  /** A custom trigger (the page's action row); default is the "…" icon button. */
  children?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const actions = useSongActions(song, { loadDetail: open });
  const artists = song.artists ?? [];
  const album = actions.detail?.album ?? null;

  return (
    <DropdownMenu dir="rtl" open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {children ?? (
          <button
            type="button"
            aria-label="گزینه‌های بیشتر"
            className={cn(
              "text-muted-foreground hover:text-foreground flex size-8 items-center justify-center rounded-full",
              triggerClassName,
            )}
          >
            <MoreHorizontal className="size-5" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn("w-60", className)}>
        <DropdownMenuItem onSelect={actions.play}>
          <Play className="size-4" />
          پخش
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={actions.addToQueue}>
          <ListEnd className="size-4" />
          افزودن به صف پخش
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={actions.addToPlaylist}>
          <ListPlus className="size-4" />
          افزودن به پلی‌لیست
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={actions.toggleSaved}>
          {actions.isSaved ? <BookmarkCheck className="size-4" /> : <Bookmark className="size-4" />}
          {actions.isSaved ? "حذف از ذخیره‌شده‌ها" : "ذخیره"}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={actions.download}>
          <Download className="size-4" />
          دانلود
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        {artists.length <= 1 ? (
          <DropdownMenuItem asChild disabled={artists.length === 0}>
            <Link href={artists[0] ? artistPath(artists[0]) : "#"}>
              <Mic2 className="size-4" />
              رفتن به صفحهٔ هنرمند
            </Link>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Mic2 className="size-4" />
              رفتن به صفحهٔ هنرمند
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {artists.map((artist) => (
                <DropdownMenuItem key={artist.id} asChild>
                  <Link href={artistPath(artist)}>{artist.name}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        <DropdownMenuItem asChild disabled={!album}>
          <Link href={album ? publicPath("albums", album) : "#"}>
            <Disc3 className="size-4" />
            رفتن به آلبوم
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={artists.length === 0}>
            <Users className="size-4" />
            عوامل
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-56">
            <DropdownMenuLabel>{song.name}</DropdownMenuLabel>
            {artists.map((artist) => (
              <DropdownMenuItem key={artist.id} asChild>
                <Link href={artistPath(artist)} className="flex-col items-start gap-0">
                  <span>{artist.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {ROLE_LABEL[artist.role] ?? artist.role}
                  </span>
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={actions.openInApp}>
          <MonitorSmartphone className="size-4" />
          ادامه در اپلیکیشن
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Share2 className="size-4" />
            اشتراک‌گذاری
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onSelect={actions.copyLink}>
              <Link2 className="size-4" />
              کپی لینک آهنگ
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={actions.copyUri}>
              <Copy className="size-4" />
              کپی آدرس {BRAND}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
